import './SelectNumberRadio.scss';

import * as React from 'react';
import { useEffect, useState } from 'react';

import {
  SlRadioButton,
  SlRadioGroup,
} from '@shoelace-style/shoelace/dist/react';
import cx from 'classnames';

type HTMLElementWithSlot<T extends string> = React.ReactElement<
  React.HTMLAttributes<HTMLElement> & { slot: T }
>;

type SlRadioGroupProps = React.ComponentProps<typeof SlRadioGroup>;

/** this type combines the Props of the SlRadioGroup component with the props of the SelectNumberRadioGroup component
 * If a label is not provided, the 'aria-labelledby' prop must be provided.
 */
type SelectNumberRadioGroupProps = Omit<
  SlRadioGroupProps,
  'label' | 'onChange' | 'aria-labelledby'
> & {
  disabled?: boolean;
  selectedNumber: number;
  numberOptions: Array<number>;
  onChange: (number: number) => void;
} & (
    | {
        label: string | HTMLElementWithSlot<'label'>;
        ['aria-labelledby']?: never;
      }
    | {
        label?: never;
        ['aria-labelledby']: string;
      }
  );

/**
 * this component is a wrapper around the SlRadioGroup component that adds the ability to select a number from a list of options
 * @param id - the id of the radio group
 * @param disabled - whether the radio group is disabled
 * @param selectedNumber - the number that is currently selected
 * @param numberOptions - the list of numbers to select from
 * @param onChange - the function to call when the selected number changes
 * @param label - the label of the radio group
 * @param 'aria-labelledby' - the aria-labelledby of the radio group
 * @param name - the name of the radio group
 * @param className - the class name of the radio group
 */
export function SelectNumberRadioGroup({
  id,
  disabled = false,
  selectedNumber,
  numberOptions,
  onChange,
  label,
  'aria-labelledby': ariaLabelledby,
  name,
  className: classNameProp,
}: SelectNumberRadioGroupProps) {
  const className = cx('select-number-radio-group', classNameProp);
  const [isReady, setIsReady] = useState(false);

  /* Wait for Shoelace components to be defined before showing
   * This ensures the web component's shadow DOM is fully rendered
   * See: https://shoelace.style/getting-started/usage#waiting-for-components-to-load
   */
  useEffect(() => {
    let mounted = true;
    let rafId1: number;
    let rafId2: number;

    Promise.all([
      customElements.whenDefined('sl-radio-group'),
      customElements.whenDefined('sl-radio-button'),
    ]).then(() => {
      // Double rAF ensures the browser has painted before showing
      rafId1 = requestAnimationFrame(() => {
        rafId2 = requestAnimationFrame(() => {
          if (mounted) {
            setIsReady(true);
          }
        });
      });
    });
    return () => {
      mounted = false;
      cancelAnimationFrame(rafId1);
      cancelAnimationFrame(rafId2);
    };
  }, []);

  return (
    <SlRadioGroup
      className={className}
      id={id}
      aria-labelledby={ariaLabelledby}
      name={name}
      label={typeof label === 'string' ? label : undefined}
      value={`${selectedNumber}`}
      onSlChange={(event) => {
        onChange(parseInt((event.target as any).value, 10));
      }}
      style={{ visibility: isReady ? 'visible' : 'hidden' }}
    >
      {React.isValidElement(label) &&
      typeof label.type === 'string' &&
      label.props.slot === 'label'
        ? label
        : null}
      {numberOptions.map((number) => (
        <SlRadioButton
          disabled={disabled}
          className="select-number-radio-button"
          checked={selectedNumber === number}
          value={`${number}`}
          key={number}
          size="large"
        >
          {number}
        </SlRadioButton>
      ))}
    </SlRadioGroup>
  );
}
